import { useState, useEffect } from 'react';

// ─── Sandsea types ────────────────────────────────────────────────────────────

export interface SandseaMember {
  name: string;
  isLeader: boolean;
}

export interface SandseaSquad {
  squadLetter: string; // A–F
  members: SandseaMember[];
}

export interface SandseaLegion {
  legion: number;
  date: string;
  time: string;
  tactician: string;
  squads: SandseaSquad[];
}

export interface SandseaRosterData {
  legions: SandseaLegion[];
  lastUpdated: string | null;
  error: string | null;
  loading: boolean;
}

function parseSandseaCSV(raw: string): SandseaLegion[] {
  const lines = raw
    .split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith('#') && l.replace(/,/g, '').length > 0);

  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const col = (name: string) => headers.indexOf(name);

  const iLegion = col('legion');
  const iDate   = col('date');
  const iTime   = col('time');
  const iSquad  = col('squad');
  const iName   = col('name');
  const iTactic = col('tactician');

  if ([iLegion, iDate, iTime, iSquad, iName].some(i => i === -1)) return [];

  const legionMap = new Map<number, SandseaLegion>();

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim());
    if (cols.length < 5) continue;

    const legionNum    = parseInt(cols[iLegion], 10);
    const date         = cols[iDate];
    const time         = cols[iTime];
    const squadLetter  = cols[iSquad]?.toUpperCase();
    const name         = cols[iName];
    const tacticianVal = iTactic !== -1 ? cols[iTactic]?.trim() : '';

    if (!name || !squadLetter || isNaN(legionNum)) continue;

    if (!legionMap.has(legionNum)) {
      legionMap.set(legionNum, { legion: legionNum, date, time, tactician: '', squads: [] });
    }

    const legion = legionMap.get(legionNum)!;

    // Tactician column — store first non-empty value found for this legion
    if (tacticianVal && !legion.tactician) {
      legion.tactician = tacticianVal;
    }

    // Squad letter "T" is reserved for tactician-only rows (no squad)
    if (squadLetter === 'T') continue;

    let squad = legion.squads.find(s => s.squadLetter === squadLetter);
    if (!squad) {
      squad = { squadLetter, members: [] };
      legion.squads.push(squad);
    }

    // First member listed per squad is the leader
    const isLeader = squad.members.length === 0;
    squad.members.push({ name, isLeader });
  }

  const legions = Array.from(legionMap.values()).sort((a, b) => a.legion - b.legion);
  legions.forEach(l => l.squads.sort((a, b) => a.squadLetter.localeCompare(b.squadLetter)));
  return legions;
}

export function useSandseaRoster(): SandseaRosterData {
  const [state, setState] = useState<SandseaRosterData>({
    legions: [],
    lastUpdated: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    fetch(`/sandsea-roster.csv?t=${Date.now()}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status} — roster file not found`);
        return r.text();
      })
      .then(text => {
        const legions = parseSandseaCSV(text);
        setState({
          legions,
          lastUpdated: new Date().toLocaleTimeString(),
          error: legions.length === 0 ? 'Roster is empty or CSV format is invalid.' : null,
          loading: false,
        });
      })
      .catch(err => {
        setState(s => ({ ...s, loading: false, error: String(err.message) }));
      });
  }, []);

  return state;
}

export interface RosterMember {
  name: string;
  role: 'Commander' | 'Warrior' | 'Engineer';
}

export interface RosterSquad {
  squadNumber: number;
  members: RosterMember[];
}

export interface RosterLegion {
  legion: number;
  date: string;
  time: string;
  squads: RosterSquad[];
}

export interface RosterData {
  legions: RosterLegion[];
  lastUpdated: string | null;
  error: string | null;
  loading: boolean;
}

function parseCSV(raw: string): RosterLegion[] {
  const lines = raw
    .split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith('#') && l.replace(/,/g, '').length > 0);

  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const col = (name: string) => headers.indexOf(name);

  const iLegion = col('legion');
  const iDate   = col('date');
  const iTime   = col('time');
  const iSquad  = col('squad');
  const iRole   = col('role');
  const iName   = col('name');

  if ([iLegion, iDate, iTime, iSquad, iRole, iName].some(i => i === -1)) return [];

  const legionMap = new Map<number, RosterLegion>();

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim());
    if (cols.length < 6) continue;

    const legionNum = parseInt(cols[iLegion], 10);
    const date      = cols[iDate];
    const time      = cols[iTime];
    const squadNum  = parseInt(cols[iSquad], 10);
    const role      = cols[iRole] as RosterMember['role'];
    const name      = cols[iName];

    if (!name || !role || isNaN(legionNum) || isNaN(squadNum)) continue;

    if (!legionMap.has(legionNum)) {
      legionMap.set(legionNum, { legion: legionNum, date, time, squads: [] });
    }

    const legion = legionMap.get(legionNum)!;
    let squad = legion.squads.find(s => s.squadNumber === squadNum);
    if (!squad) {
      squad = { squadNumber: squadNum, members: [] };
      legion.squads.push(squad);
    }
    squad.members.push({ name, role });
  }

  const legions = Array.from(legionMap.values()).sort((a, b) => a.legion - b.legion);
  legions.forEach(l => l.squads.sort((a, b) => a.squadNumber - b.squadNumber));
  return legions;
}

export function formatEventDateTime(date: string, time: string): string {
  try {
    // Normalize M/D/YY or M/D/YYYY → YYYY-MM-DD so Date parsing is reliable
    let iso = date;
    const slashMatch = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      let year = parseInt(slashMatch[3], 10);
      if (year < 100) year += 2000;
      const month = slashMatch[1].padStart(2, '0');
      const day   = slashMatch[2].padStart(2, '0');
      iso = `${year}-${month}-${day}`;
    }
    const dt = new Date(`${iso}T00:00:00`);
    if (isNaN(dt.getTime())) return `${date} · ${time} ST`;
    const dayStr = dt.toLocaleString('en-US', {
      weekday: 'long',
      month:   'short',
      day:     'numeric',
      year:    'numeric',
    });
    return `${dayStr} · ${time} ST`;
  } catch {
    return `${date} · ${time} ST`;
  }
}

export function useRoster(): RosterData {
  const [state, setState] = useState<RosterData>({
    legions: [],
    lastUpdated: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    fetch(`/crystal-clash-roster.csv?t=${Date.now()}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status} — roster file not found`);
        return r.text();
      })
      .then(text => {
        const legions = parseCSV(text);
        setState({
          legions,
          lastUpdated: new Date().toLocaleTimeString(),
          error: legions.length === 0 ? 'Roster is empty or CSV format is invalid.' : null,
          loading: false,
        });
      })
      .catch(err => {
        setState(s => ({ ...s, loading: false, error: String(err.message) }));
      });
  }, []);

  return state;
}
